import { useNavigate } from "react-router-dom";
import { mdiChevronRight, mdiPulse, mdiDeleteOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useMutation, useQueryClient } from "react-query";

import type { ListarGruposResponse } from "../../model/listarGruposResponse";
import { deleteGruposDeletar, getGetGruposListarQueryKey } from "../../config/swagger";
import { useToast } from "../../context/ToastContext";
import { getSaudeIcon, getSaudeDescricao } from "../../utils/saude";
import ListCard from "../ListCard";

type GruposCardPropsContent = {
	data: ListarGruposResponse;
	skeleton?: never;
};

type GruposCardPropsSkeleton = {
	data?: never;
	skeleton: true;
};

type GruposCardProps = GruposCardPropsContent | GruposCardPropsSkeleton;

export default function GruposCard(props: GruposCardProps) {
	const { handleToast } = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [removeModal, setRemoveModal] = useState(false);
	const [removed, setRemoved] = useState(false);

	const deleteMutation = useMutation((id: number) => deleteGruposDeletar({ id }), {
		onSuccess: () => {
			queryClient.invalidateQueries(getGetGruposListarQueryKey());
			setRemoved(true);
			handleToast("Grupo", "Grupo removido com sucesso!", 5000);
			setRemoveModal(false);
		},
		onError: () => {
			handleToast("Grupo", "Erro ao remover grupo", 5000, "danger");
			setRemoveModal(false);
		},
	});

	function handleClick() {
		if (!props.skeleton && props.data) {
			navigate(`/grupoFormulario/${props.data.id}`);
		}
	}

	function handleClickRemove(e: React.MouseEvent) {
		e.stopPropagation();
		setRemoveModal(true);
	}

	async function handleRemove() {
		if (!props.skeleton && props.data?.id) {
			deleteMutation.mutate(props.data.id);
		}
	}

	function handleCloseModal() {
		if (!deleteMutation.isLoading) {
			setRemoveModal(false);
		}
	}

	if (removed) {
		return <></>;
	}

	let skeletonButton = "";
	if (props.skeleton) {
		skeletonButton = "skeleton skeleton-button";
	}

	const iconClass = getSaudeIcon(props.data?.saude);
	const saudeDescricao = getSaudeDescricao(props.data?.saude);

	return (
		<>
			<ListCard onClick={handleClick}>
				<ListCard.Content>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Nome</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.nome ?? "..."}</ListCard.Text>
					</ListCard.Item>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Saúde</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>
							<Icon path={mdiPulse} size={1} className={`me-1 ${iconClass}`} /> {saudeDescricao}
						</ListCard.Text>
					</ListCard.Item>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Latência</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.ultimaLatencia ?? "..."}</ListCard.Text>
					</ListCard.Item>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Data Hora Checagem</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.dataUltimaChecagem ?? "..."}</ListCard.Text>
					</ListCard.Item>
				</ListCard.Content>
				<ListCard.Action>
					<Button variant="light" className={`me-2 ${skeletonButton}`} onClick={handleClickRemove}>
						<Icon path={mdiDeleteOutline} size={1} className="text-danger" />
					</Button>
					<Icon path={mdiChevronRight} size={1} className={`text-muted m-2 ${skeletonButton}`} />
				</ListCard.Action>
			</ListCard>

			<Modal show={removeModal} onHide={handleCloseModal} centered>
				<Modal.Header closeButton>
					<Modal.Title>Remover Grupo</Modal.Title>
				</Modal.Header>
				<Modal.Body className="text-center">{!deleteMutation.isLoading ? "Deseja realmente remover este grupo?" : <Spinner />}</Modal.Body>
				{!deleteMutation.isLoading && (
					<Modal.Footer>
						<Button variant="secondary" onClick={handleCloseModal}>
							Cancelar
						</Button>
						<Button variant="danger" onClick={handleRemove}>
							Remover
						</Button>
					</Modal.Footer>
				)}
			</Modal>
		</>
	);
}
